import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { ActiveDataRoomUser, CurrentUser } from '../../../shared/models';
import { FormikUtils, HtmlUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface BankTermSheetFormProps {
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
	dataRoomUser: ActiveDataRoomUser | null;
	onClose: () => void;
}

export const BankTermSheetForm: React.FC<BankTermSheetFormProps> = ({
	dataRoomGuid,
	currentUser,
	dataRoomUser,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);

	const termSheetMutation = useMutation(
		async ({ values }: BankTermSheetFormMutationVariables) => {
			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<void>(
					api.post(CerebroApiEndpoints.TermSheets.Upload(dataRoomGuid, dataRoomUser?.guid), formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return null;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.Revisions(dataRoomGuid));
				snackbar.enqueueSnackbar('Term sheet was added.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={dataRoomUser != null} onClose={() => onClose()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				{dataRoomUser?.fullName} {dataRoomUser?.bankName}
			</CerebroHeading>
			<Formik
				initialValues={{
					uploads: [] as File[],
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(termSheetMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
							scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel required id="uploads">
										Term Sheet File
									</CerebroLabel>
									<Field
										as={CerebroFileDragAndDrop}
										name="uploads"
										onlyPdf={true}
										multiple={false}
										sx={{ marginY: 3, width: '100%' }}
										error={touched.uploads && errors.uploads !== undefined}
									/>
									{errors.uploads && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.uploads}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Upload
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type BankTermSheetFormMutationVariables = FormikUtils.MutateOnSubmitVariables<BankTermSheetFormValues>;
interface BankTermSheetFormValues {
	uploads: File[];
}
type BankTermSheetFormValueErrors = FormikErrors<BankTermSheetFormValues>;

function validateForm(values: BankTermSheetFormValues) {
	const errors: BankTermSheetFormValueErrors = {};

	if (!values.uploads || values.uploads.length == 0) {
		errors.uploads = 'You must select a term sheet file.';
	}

	return errors;
}
